import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LogIn } from "lucide-react";
import { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    async function doLogin(e) {
        if (e) e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const data = await login(email, password);
            const destination = data?.user?.must_change_password
                ? "/trocar-senha"
                : (location.state?.from?.pathname || "/");
            navigate(destination, { replace: true });
        } catch (err) {
            setError(formatApiErrorDetail(err?.response?.data?.detail) || "Falha no login");
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="min-h-screen w-full relative overflow-hidden bg-white flex items-center justify-center px-6">
            {/* Decorative logo background — subtle, large, off-center */}
            <div className="pointer-events-none absolute -top-44 -right-44 w-[680px] h-[680px] opacity-[0.06] rotate-12 select-none">
                <img src="/logo-licito.png" alt="" className="w-full h-full object-contain" style={{ filter: "saturate(1.6)" }} />
            </div>
            <div className="pointer-events-none absolute -bottom-56 -left-56 w-[640px] h-[640px] opacity-[0.05] -rotate-12 select-none">
                <img src="/logo-licito.png" alt="" className="w-full h-full object-contain" />
            </div>
            {/* Subtle gradient glow */}
            <div className="pointer-events-none absolute inset-0" style={{
                background: "radial-gradient(900px 600px at 18% 22%, rgba(45, 212, 191, 0.10), transparent 60%), radial-gradient(900px 600px at 82% 78%, rgba(34, 197, 94, 0.10), transparent 60%)"
            }} />

            <div className="relative z-10 w-full max-w-md">
                {/* Logo */}
                <div className="flex flex-col items-center mb-10 fade-in">
                    <img src="/logo-licito.png" alt="licito.guru IA" className="h-20 w-auto" />
                </div>

                {/* Tagline with gradient on SUCESSO and CLIENTE */}
                <h1 className="text-center font-display font-semibold text-[28px] leading-tight text-[#0F2044] mb-8 fade-in" data-testid="login-tagline">
                    Vamos garantir o{" "}
                    <span className="font-extrabold bg-clip-text text-transparent" style={{
                        backgroundImage: "linear-gradient(95deg, #06B6D4 0%, #14B8A6 35%, #22C55E 100%)"
                    }}>
                        SUCESSO
                    </span>{" "}
                    do nosso{" "}
                    <span className="font-extrabold bg-clip-text text-transparent" style={{
                        backgroundImage: "linear-gradient(95deg, #22C55E 0%, #14B8A6 50%, #06B6D4 100%)"
                    }}>
                        CLIENTE
                    </span>{" "}
                    <span className="font-display text-[#0F2044]">hoje?</span>
                </h1>

                <div className="bg-white border border-slate-200 rounded-xl shadow-[0_8px_30px_rgba(15,32,68,0.06)] p-7 fade-in">
                    <form onSubmit={doLogin} className="space-y-4" data-testid="login-form">
                        <div>
                            <label className="block text-[11px] font-semibold text-slate-700 mb-1.5 uppercase tracking-[0.12em]">E-mail</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                data-testid="login-email-input"
                                className="w-full px-3.5 py-2.5 rounded-md border border-slate-300 focus:border-[#0F2044] focus:ring-2 focus:ring-[#0F2044]/15 focus:outline-none text-sm"
                                placeholder="seu@email.com"
                            />
                        </div>
                        <div>
                            <label className="block text-[11px] font-semibold text-slate-700 mb-1.5 uppercase tracking-[0.12em]">Senha</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                data-testid="login-password-input"
                                className="w-full px-3.5 py-2.5 rounded-md border border-slate-300 focus:border-[#0F2044] focus:ring-2 focus:ring-[#0F2044]/15 focus:outline-none text-sm"
                                placeholder="••••••••"
                            />
                        </div>
                        {error && (
                            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="login-error">
                                {error}
                            </div>
                        )}
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="login-submit-button"
                            className="w-full bg-[#0F2044] hover:bg-[#172E5E] text-white text-sm font-semibold py-2.5 rounded-md flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                        >
                            <LogIn className="h-4 w-4" />
                            {loading ? "Entrando..." : "Entrar"}
                        </button>
                    </form>
                </div>

                <p className="text-center text-[11px] text-slate-400 mt-6">
                    © licito.guru IA — Console de Customer Success
                </p>
            </div>
        </div>
    );
}
